import React from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { Popper } from 'react-popper';
import { useOverlayPortalTarget } from '../../hooks/useOverlayPortalTarget';
import { Placement, Position, Behavior } from 'popper.js';
import './Callout.less';
import { OverlayBackdrop, OverlayClickPropagationSetting } from './OverlayBackdrop';
import { CalloutCaret, CALLOUT_CARET_SIZE } from './CalloutCaret';
import { CalloutContent } from 'components/layout/CalloutContent';

/**
 * Props for callout behavior. Other HTMLProps will be applied to the callout body element.
 */
export interface ICalloutProps extends React.HTMLProps<HTMLDivElement> {
  /** Whether to show it based on parent state */
  isOpen: boolean;
  /** Element to anchor the callout */
  sourceRef: React.MutableRefObject<HTMLElement | null> | DOMRect;
  /** Handler to close overlay on click outside contents */
  onClose: () => void;
  /** Contents */
  children: React.ReactNode;
  /** Additional styling */
  className?: string;
  /** Fixed height */
  // callOutHeight?: number;
  showArrow?: boolean;
  /**
   * How many callouts-deep is this one. Only the first level will
   * be rendered in a portal at the document root.
   */
  depth?: number;
  /** Which side of the sourceRef to show the callout */
  placement?: Placement;
  /** If you need to specify where the callout goes when there's no */
  flipBehavior?: Behavior | Position[];
  // TODO background color, so caret can match different colored callouts
  overlayClassName?: string;
  width?: number;
  /** Apply class as per popper position */
  getClassNameAsPerPlacement?: (placement: string) => string | undefined;
  backdropIsVisible?: boolean;
  /**
   * True to add scrollbar directly to Callout body, instead of managing it inside the callout contents.
   *
   * Defaults to `true`
   */
  scrollContents?: boolean;
  /**
   * Which mouse events, right, left or both, should we allowed to propagate from the overlay backdrop
   */
  overlayClickPropagation?: OverlayClickPropagationSetting;
  sourceClone?: React.FunctionComponentElement<any>;
}

const getClassName = (className?: string, classNameAsPerPlacement?: string) =>
  classNames('dpc-callout', className, classNameAsPerPlacement);
const getBodyClassName = (className?: string) =>
  classNames('dpc-callout__body', {
    [`${className}_body`]: !!className,
  });

/** Provide flip behavior if defined, or use default */
const getFlip = (flipBehavior?: Behavior | Position[]) =>
  flipBehavior
    ? {
        behavior: flipBehavior,
      }
    : undefined;

/**
 * Simple callout component.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export const Callout = React.forwardRef(
  (
    {
      backdropIsVisible,
      children,
      className,
      depth,
      flipBehavior,
      isOpen,
      onClose,
      placement,
      showArrow,
      sourceRef,
      overlayClassName,
      width,
      scrollContents,
      getClassNameAsPerPlacement,
      overlayClickPropagation,
      sourceClone,
      ...otherHtmlProps
    }: ICalloutProps,
    ref: React.Ref<HTMLElement>
  ) => {
    // as this callout will not render a second portal/overlay, if it's nested (action links, etc.),
    // we should never to tell useOverlayPortalTarget to open if the depth is more than 0.
    const openPortalTarget = isOpen && !depth && isReferenceExist(sourceRef);
    const { overlayPortal } = useOverlayPortalTarget(openPortalTarget, { className: 'dpc-callout-holder' });

    if (!isOpen || !isReferenceExist(sourceRef)) {
      // don't render if not open, obviously
      // don't render if the source ref isn't populated, because we don't know where, yet.
      return null;
    }

    const content = (
      <Popper
        innerRef={ref}
        placement={placement}
        eventsEnabled={true} // because popper changing events target on referenceElement without eventsEnabled={true}
        referenceElement={getReferenceElement(sourceRef)}
        modifiers={{
          offset: {
            enabled: showArrow,
            // "length, width" relative to the "placement" orientation https://popper.js.org/docs/v1/#modifiers..offset
            offset: `0, ${CALLOUT_CARET_SIZE * 0.8}px`,
          },
          flip: getFlip(flipBehavior),
          preventOverflow: {
            boundariesElement: 'viewport',
          },
        }}
      >
        {({ ref, style, placement, arrowProps, scheduleUpdate }) => (
          <CalloutContent
            ref={ref}
            className={getClassName(className, getClassNameAsPerPlacement && getClassNameAsPerPlacement(placement))}
            placement={placement}
            style={{ ...style, width }}
            scheduleUpdate={scheduleUpdate}
          >
            {showArrow && <CalloutCaret {...arrowProps} placement={placement} />}
            <div className={getBodyClassName(className)} style={getMaxSizeStyles(scrollContents)} {...otherHtmlProps}>
              {children}
            </div>
          </CalloutContent>
        )}
      </Popper>
    );

    // if it's a zero depth, it needs to be rendered into a portal at the root of the body
    // it also needs to have a click-catching backdrop
    return depth
      ? content
      : createPortal(
          <OverlayBackdrop
            className={overlayClassName}
            isOpen={isOpen}
            onClose={onClose}
            isVisible={backdropIsVisible}
            allowEventPropagation={overlayClickPropagation}
          >
            {sourceClone}
            {content}
          </OverlayBackdrop>,
          overlayPortal
        );
  }
);

function getMaxSizeStyles(scrollContents = true): React.CSSProperties {
  return {
    maxHeight: window.innerHeight,
    maxWidth: window.innerWidth,
    overflow: scrollContents ? 'auto' : undefined,
  };
}

function isReferenceExist(referenceElement: React.MutableRefObject<HTMLElement | null> | DOMRect) {
  return !!(referenceElement as React.MutableRefObject<HTMLElement | null>).current || referenceElement instanceof DOMRect
}

function getReferenceElement(referenceElement: React.MutableRefObject<HTMLElement | null> | DOMRect) {
  return referenceElement instanceof DOMRect ? {clientHeight: 0, clientWidth: 0, getBoundingClientRect: () => referenceElement} : referenceElement.current as HTMLElement
}
